// src/shoppingFlow/OrdersListPage.js
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Package, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import API_BASE from '../apiBase';
import QaAlert from '../components/QaAlert';

function fmtMoney(n, c = 'CAD') {
  const v = Number(n ?? 0);
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(v); }
  catch { return `$${v.toFixed(2)}`; }
}

function StatusPill({ status }) {
  const map = {
    Paid:       'bg-green-100 text-green-700',
    Shipped:    'bg-blue-100 text-blue-700',
    Delivered:  'bg-emerald-100 text-emerald-700',
    Cancelled:  'bg-red-100 text-red-700',
    Pending:    'bg-yellow-100 text-yellow-700',
    Processing: 'bg-gray-100 text-gray-700',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${cls}`} data-qa="orders-item-status">
      {status || '—'}
    </span>
  );
}

// decide if the cancel button should show
function canCancel(order) {
  if (!order) return false;
  if (order.status !== 'Paid') return false;
  if (order.shipping_status && order.shipping_status !== 'Processing') return false;
  if (!order.order_date) return false;
  const placedAt = new Date(order.order_date).getTime();
  if (Number.isNaN(placedAt)) return false;
  const diffMs = Date.now() - placedAt;
  const tenMinutesMs = 10 * 60 * 1000;
  return diffMs <= tenMinutesMs;
}

export default function OrdersListPage() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  // Memoized loader to satisfy exhaustive-deps rule
  const loadPage = useCallback(async (p) => {
    setLoading(true);
    setErr('');
    setMsg('');
    try {
      const meRes = await fetch(`${API_BASE}/api/auth/me`, { credentials: 'include' });
      if (meRes.status === 401) {
        navigate('/signin', { replace: true, state: { next: '/orders' } });
        return;
      }
      const res = await fetch(`${API_BASE}/api/users/me/orders?page=${p}&page_size=${pageSize}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to load orders');
      setOrders(data.data.orders || []);
      setTotal(Number(data.data.total || 0));
    } catch (e) {
      setErr(e.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [pageSize, navigate]);

  useEffect(() => {
    loadPage(page);
  }, [page, loadPage]);

  async function cancelOrder(orderId) {
    setErr('');
    setMsg('');
    try {
      const confirm = window.confirm('Cancel this order? This cannot be undone.');
      if (!confirm) return;

      const res = await fetch(`${API_BASE}/api/orders/${orderId}/cancel`, {
        method: 'PATCH',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Cancel failed');

      // update order in place
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'Cancelled', shipping_status: 'Cancelled' } : o));
      setMsg(`Order #${orderId} cancelled.`);
    } catch (e) {
      setErr(e.message || 'Cancel failed');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" data-qa="orders-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/userprofile')}
              className="text-gray-600 hover:text-gray-800 flex items-center gap-1"
              data-qa="orders-back-to-profile"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Profile
            </button>
          </div>
          <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
            SodaSips
          </Link>
          <Link
            to="/"
            className="text-sm text-orange-600 hover:underline"
            data-qa="orders-continue-shopping"
          >
            Continue Shopping
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Always-mounted banners */}
        <QaAlert id="orders-success" dataQa="qa-orders-success" message={msg} variant="success" />
        <QaAlert id="orders-error"   dataQa="qa-orders-alert"   message={err} variant="error" />

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600" data-qa="orders-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading orders…
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center" data-qa="orders-empty">
            <Package className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <h2 className="text-lg font-semibold text-gray-800">No orders yet</h2>
            <p className="text-gray-600 mt-1">When you place an order, it’ll show up here.</p>
            <Link
              to="/"
              className="mt-4 inline-block bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
              data-qa="orders-empty-shop"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-4" data-qa="orders-list">
            {orders.map(o => {
              const showCancel = canCancel(o);
              return (
                <div
                  key={o.id}
                  className="bg-white rounded-xl shadow p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                  data-qa="orders-item"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="text-sm text-gray-500">
                        Order <span className="font-semibold text-gray-800" data-qa="orders-item-id">#{o.id}</span>
                      </div>
                      <div className="text-sm text-gray-500" data-qa="orders-item-date">
                        {o.order_date ? new Date(o.order_date).toLocaleString() : '—'}
                      </div>
                      <StatusPill status={o.status} />
                      {o.shipping_status && (
                        <span className="text-xs text-gray-500" data-qa="orders-item-ship-status">
                          Ship: {o.shipping_status}
                        </span>
                      )}
                    </div>

                    <div className="mt-1 text-sm text-gray-600">
                      Tracking:&nbsp;
                      <span className="font-mono" data-qa="orders-item-tracking">
                        {o.tracking_number || '—'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Total</div>
                      <div className="text-base font-semibold" data-qa="orders-item-total">
                        {fmtMoney(o.total_price, o.currency)}
                      </div>
                    </div>

                    <Link
                      to={`/order-confirmation/${o.id}`}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                      data-qa="orders-item-link"
                    >
                      View details
                    </Link>

                    {showCancel && (
                      <button
                        onClick={() => cancelOrder(o.id)}
                        className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white"
                        data-qa="orders-item-cancel"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            <div className="flex items-center justify-center gap-2 pt-2" data-qa="orders-pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 disabled:opacity-50"
                data-qa="orders-page-prev"
              >
                <span className="inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Prev</span>
              </button>
              <span className="text-sm text-gray-600" data-qa="orders-page-indicator">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 disabled:opacity-50"
                data-qa="orders-page-next"
              >
                <span className="inline-flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></span>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
